// useUserNames.ts — resolve actor uuids (created_by / approver / audit who)
// to usernames. Backed by /api/identity/users/; mock rows already carry
// display names so the resolver passes non-uuid values straight through.

import { useQuery } from '@tanstack/react-query';
import { apiFetch, apiFetchList } from './client';
import { useRealApi } from './paths';
import { unwrapPage } from './adapters';

export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Display helper: collapse a raw UUID to a short token (keep the full value for `title`),
 * but leave human-readable references (Rx tokens, batch numbers) untouched. */
export const shortId = (v: string | null | undefined): string =>
  !v ? '—' : UUID_RE.test(v) ? `${v.slice(0, 8)}…` : v;

interface UserRow {
  id: string;
  username: string;
}

export function useUserName(): (idOrName: string | null | undefined) => string {
  const { data: users = [] } = useQuery({
    queryKey: ['identity', 'user-names'],
    enabled: useRealApi,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<UserRow[]> => {
      const data = await apiFetchList<unknown>('/api/identity/users/', {
        query: { page_size: 200 },
      });
      return unwrapPage<UserRow>(data);
    },
  });
  return (idOrName) => {
    if (!idOrName) return '—';
    if (!UUID_RE.test(idOrName)) return idOrName;
    return users.find((u) => u.id === idOrName)?.username ?? `${idOrName.slice(0, 8)}…`;
  };
}
